import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";

import { RadioGroup } from "../../../../components";
import { getManagementFeeOptions } from "../OrderForm.options";
import { OrderFormData } from "../OrderForm.schema";
import { Portfolio } from "@/api/types";

type Props = {
  portfolios: Portfolio[];
};

export function FeeSection({ portfolios }: Props) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" });
  const modelType = useWatch({ name: "dpModelType" });
  const feeType = useWatch({ name: "dpFeeType" });
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) || 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) || 0;
  const totalPortValue = sumAvCash + sumInsValue + amountToInvest;

  const [amountFocused, setAmountFocused] = useState(false);

  const selected = portfolios.find((p) => p.id === Number(portfolioId));
  const managementFee = selected?.fees.find((f) => f.type === "Management");
  const feeOptions = getManagementFeeOptions(managementFee?.feePercent);

  // If model is set → fee type is always "all" and disabled
  const modelIsSet = !!modelType && modelType !== "NOMODEL";

  // Auto-set management fee when portfolio changes
  useEffect(() => {
    if (!portfolioId || !selected) {
      // New portfolio → default to Listepris
      setValue("dpManagementFee", "1.5");
    } else if (managementFee?.feePercent !== undefined) {
      setValue("dpManagementFee", String(managementFee.feePercent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  useEffect(() => {
    if (modelIsSet) {
      setValue("dpFeeType", "all");
    }
  }, [modelIsSet, setValue]);

  return (
    <div className="space-y-3">
      {/* Amount to invest */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          New amount to invest:
        </label>
        <input
          type="number"
          min={0}
          step={1000}
          {...(() => {
            const { onBlur, ...rest } = register("dpAmountToInvest", {
              valueAsNumber: true,
            });
            return {
              ...rest,
              onFocus: () => setAmountFocused(true),
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                setAmountFocused(false);
                onBlur(e);
              },
            };
          })()}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {/* Minimum investment warnings — only shown after blur */}
      {!amountFocused &&
        (() => {
          const isEKB = modelType?.includes("EKB");
          const isEiendom = modelType?.includes("CAPGUARDEIENDOM");
          const threshold = isEKB ? 1_000_000 : isEiendom ? 750_000 : 0;
          const label = isEKB ? "Aktiv EKB" : isEiendom ? "Aktiv Eiendom" : "";
          if (!threshold || totalPortValue === 0 || totalPortValue >= threshold)
            return null;
          const shortfall = threshold - totalPortValue;
          return (
            <div className="grid grid-cols-[180px_1fr] items-start gap-3">
              <span />
              <div className="flex gap-2.5 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/40 px-3.5 py-2.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-200 space-y-0.5">
                  <p className="font-semibold">
                    {label} krever minimum {threshold.toLocaleString("nb-NO")}{" "}
                    kr
                  </p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Mangler{" "}
                    <span className="font-medium tabular-nums">
                      {shortfall.toLocaleString("nb-NO")} kr
                    </span>{" "}
                    for å nå minimumsbeløpet.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Subscription Fee % */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Subscription Fee %:
        </label>
        <input
          type="text"
          {...register("dpOnOffPercentage")}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {errors.dpOnOffPercentage && (
          <p className="col-start-2 text-xs text-red-500">
            {errors.dpOnOffPercentage.message}
          </p>
        )}
      </div>

      {/* Management fee */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Management fee:
        </label>
        <select
          {...register("dpManagementFee")}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {feeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.dpManagementFee && (
          <p className="col-start-2 text-xs text-red-500">
            {errors.dpManagementFee.message}
          </p>
        )}
      </div>

      {/* Fee type */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Fee type:
        </label>
        <RadioGroup
          options={[
            { label: "Only fund", value: "onlyFund" },
            { label: "All investments", value: "all" },
          ]}
          disabled={modelIsSet}
          value={feeType}
          {...register("dpFeeType")}
        />
      </div>
    </div>
  );
}
